import { useState, useEffect } from "react";
import {
  Fab,
  Badge,
  List,
  ListItem,
  ListItemText,
  Drawer,
  Typography,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import { getFABChatData } from "services/api";
import PropTypes from "prop-types";
import { Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/authContext";
import { useTranslation } from "react-i18next";

const ChatFAB = ({ employeeId, direction }) => {
  const isRTL = direction == "rtl";
  const [open, setOpen] = useState(false);
  const { chats, setChats, unreadCount, setUnreadCount, chatschanged } = useAuth();
  const [chatFilter, setChatFilter] = useState("active");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const toArabicNumbers = (num) => num.toString().replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[d]);

  useEffect(() => {
    fetchChats();
  }, [chatschanged]);

  const fetchChats = async () => {
    setLoading(true);
    const data = await getFABChatData(employeeId);
    if (data != "error") {
      setChats(data);
      setUnreadCount(data.reduce((sum, chat) => sum + chat.unreadMessages, 0));
    }
    setLoading(false);
  };

  const handleOpenChat = (chat) => {
    navigate(`/chat/${chat.chatRoomId}`, { state: { chat } });
  };

  const filteredChats = chats.filter((chat) =>
    chatFilter === "active" ? chat.active : !chat.active
  );

  return (
    <>
      {/* Floating Action Button to Open Drawer */}
      <Fab
        color="primary"
        aria-label="chats"
        onClick={() => setOpen(true)}
        style={{ position: "fixed", bottom: 20, [isRTL ? "left" : "right"]: 20 }}
      >
        <Badge badgeContent={unreadCount} color="secondary">
          <ChatIcon />
        </Badge>
      </Fab>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {chatFilter === "active" ? t("user.chats.activeChats") : t("user.chats.inactiveChats")}
          </Typography>
          <IconButton onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </div>

        {/* Toggle Button Group for Active/Inactive Chats */}
        <ToggleButtonGroup
          value={chatFilter}
          exclusive
          onChange={(_, newFilter) => setChatFilter(newFilter)}
          sx={{ mb: 2, justifyContent: "center", width: "100%" }}
        >
          <ToggleButton value="active" aria-label="active chats">
            {t("user.chats.active")}
          </ToggleButton>
          <ToggleButton value="inactive" aria-label="inactive chats">
            {t("user.chats.inactive")}
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider />

        {/* Loading Indicator */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {/* Chat List */}
        <List>
          {filteredChats.length === 0 && !loading ? (
            <Typography sx={{ textAlign: "center", padding: "16px", color: "gray" }}>
              {chatFilter === "active"
                ? t("user.chats.noActiveChats")
                : t("user.chats.noInactiveChats")}
            </Typography>
          ) : (
            filteredChats.map((chat) => (
              <ListItem
                button
                key={chat.chatRoomId}
                onClick={() => handleOpenChat(chat)}
                sx={{
                  borderRadius: "8px",
                  margin: "5px 10px",
                  backgroundColor: "#f5f5f5",
                  "&:hover": { backgroundColor: "#e0e0e0" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                {/* Chat Avatar + Name + Unread Messages */}
                <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                  <ListItemAvatar>
                    <Badge badgeContent={chat.unreadMessages} color="error" overlap="circular">
                      <Avatar sx={{ bgcolor: "primary.main" }}>
                        <ChatIcon />
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "150px",
                        }}
                      >
                        {chat.kpiName}
                      </Typography>
                    }
                    secondary={t("user.chats.unreadMsgs", {
                      count: chat.unreadMessages,
                    })}
                  />
                </Box>

                {/* Month and Year */}
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "gray",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                  mr={2}
                >
                  {i18n.language === "ar"
                    ? `${toArabicNumbers(chat.year)}/${toArabicNumbers(chat.month)}`
                    : `${chat.month}/${chat.year}`}
                </Typography>
              </ListItem>
            ))
          )}
        </List>
      </Drawer>
    </>
  );
};

ChatFAB.propTypes = {
  employeeId: PropTypes.string.isRequired,
  direction: PropTypes.string,
};

export default ChatFAB;
